import { useRef, useEffect, useCallback } from 'react';
import { useConversation } from '../../contexts/ConversationContext';
import { useLLM } from '../../hooks/useLLM';
import MessageBubble from './MessageBubble';
import PromptInput from './PromptInput';

export default function ChatPanel() {
  const {
    messages,
    isGenerating,
    sendMessage,
    updateMessage,
    setIsGenerating,
    setVisualization,
    pendingQuery,
    clearPendingQuery,
    setActiveFilters,
    addToHistory,
    dateRange,
  } = useConversation();
  const { generate } = useLLM();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = useCallback(
    async (content: string) => {
      console.log('[ChatPanel] Submitting:', content);

      // Add to query history
      addToHistory(content);

      const assistantId = await sendMessage(content);
      console.log('[ChatPanel] Assistant message created:', assistantId);

      try {
        console.log('[ChatPanel] Calling generate with dateRange:', dateRange);
        const response = await generate(content, messages, dateRange);
        console.log('[ChatPanel] Got response:', response);

        updateMessage(assistantId, {
          content: response.content || 'No response content',
          html: response.html,
          a2ui: response.a2ui,
          data: response.data,
          isStreaming: false,
        });

        // Set visualization if we have A2UI or HTML
        if (response.a2ui || response.html) {
          console.log('[ChatPanel] Setting visualization:', {
            hasA2ui: !!response.a2ui,
            hasHtml: !!response.html,
            dataKeys: Object.keys(response.data || {}),
          });
          setVisualization({
            a2ui: response.a2ui,
            html: response.html,
            data: response.data || {},
          });

          // Update active filters from A2UI response
          if (response.a2ui?.activeFilters) {
            setActiveFilters(response.a2ui.activeFilters);
          }
        }
      } catch (error) {
        console.error('[ChatPanel] Error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        updateMessage(assistantId, {
          content: `Error: ${errorMessage}\n\nMake sure you're running with "npm run dev:full" or "npm start" to enable the LLM endpoint.`,
          isStreaming: false,
        });
      } finally {
        setIsGenerating(false);
      }
    },
    [sendMessage, generate, messages, updateMessage, setIsGenerating, setVisualization, setActiveFilters, addToHistory, dateRange]
  );

  // Process pending queries from suggestions or other sources
  useEffect(() => {
    if (pendingQuery && !isGenerating) {
      clearPendingQuery();
      handleSubmit(pendingQuery);
    }
  }, [pendingQuery, isGenerating, clearPendingQuery, handleSubmit]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-3 py-2 border-b border-grey-medium/20 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-light text-black text-sm md:text-base">Chat</h2>
            <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider">
              Ask questions about your data
            </p>
          </div>
          {isGenerating && (
            <div className="flex items-center gap-2 text-mint">
              <div className="w-2 h-2 bg-mint rounded-full animate-pulse-subtle" />
              <span className="text-xs font-mono uppercase tracking-wider">Thinking</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 md:p-3 space-y-2 md:space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center animate-fade-in">
            <div className="text-center text-grey-medium px-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-grey-light flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-grey-medium"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <p className="text-sm font-light mb-2">Start a conversation</p>
              <p className="text-xs text-grey-medium/70">
                Ask about things, holders, or locations in your TES data
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }}
              >
                <MessageBubble message={message} />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-2 md:p-3 border-t border-grey-medium/20 bg-grey-light/50 flex-shrink-0">
        <PromptInput onSubmit={handleSubmit} disabled={isGenerating} />
      </div>
    </div>
  );
}
